"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkForCronGapsOrOverlaps = exports.expandCronExpression = void 0;
// Expand a single cron field into an array of numbers
function expandCronField(field, min, max) {
    const values = new Set();
    const parts = field.split(",");
    for (const part of parts) {
        if (part === "*") {
            for (let i = min; i <= max; i++)
                values.add(i);
        }
        else if (part.includes("/")) {
            const [base, stepStr] = part.split("/");
            const step = parseInt(stepStr, 10);
            if (base === "*") {
                for (let i = min; i <= max; i += step)
                    values.add(i);
            }
            else if (base.includes("-")) {
                const [start, end] = base.split("-").map(Number);
                for (let i = start; i <= end; i += step)
                    values.add(i);
            }
        }
        else if (part.includes("-")) {
            const [start, end] = part.split("-").map(Number);
            for (let i = start; i <= end; i++)
                values.add(i);
        }
        else {
            values.add(parseInt(part, 10));
        }
    }
    return new Set(Array.from(values).sort((a, b) => a - b));
}
// Expands a cron expression into its fields
function expandCronExpression(expr) {
    const fields = expr.trim().split(/\s+/);
    if (fields.length !== 5) {
        throw new Error("Cron expression must have exactly 5 fields");
    }
    const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;
    return {
        minute: expandCronField(minute, 0, 59),
        hour: expandCronField(hour, 0, 23),
        dayOfMonth: expandCronField(dayOfMonth, 1, 31),
        month: expandCronField(month, 1, 12),
        dayOfWeek: expandCronField(dayOfWeek, 0, 6),
        originalCron: expr,
    };
}
exports.expandCronExpression = expandCronExpression;
// Throws an error if there are gaps or overlaps in the cron expressions.
// This function is really slow, 20 ms on my machine for a complete set of expressions.
// Unfortunately I haven't figured out a way to ask this question without looping through every minute of the year.
function checkForCronGapsOrOverlaps(expressions) {
    const cronFields = expressions.map(expandCronExpression);
    for (let month = 1; month <= 12; month++) {
        const monthFields = cronFields.filter((f) => f.month.has(month));
        if (monthFields.length === 0) {
            throw new Error(`Gap in schedule found for month ${month}`);
        }
        const daysInMonth = (m) => new Date(2024, m, 0).getDate();
        for (let day = 1; day <= daysInMonth(month); day++) {
            const weekday = new Date(2024, month - 1, day).getDay(); // 0..6 (Sun..Sat)
            const dayFields = monthFields.filter((f) => {
                const isFullDom = f.dayOfMonth.size === 31;
                const isFullDow = f.dayOfWeek.size === 7;
                const domMatch = f.dayOfMonth.has(day);
                const dowMatch = f.dayOfWeek.has(weekday);
                // There is some subtlety in the cron logic around days of the month and days of the week.
                // For example, " * * * 3 * means to fire every minute of March. This ignores the wildcard that is on the
                // day of the week field.
                // This code handles that logic in the dom and dow fields where wildcards are ignored if the other field is specific.
                // There also exist complicated cron expressions that could involve specific days of the month and specific days of the week.
                // This is handled here in the final domMatch || dowMatch.
                return ((isFullDom && isFullDow) ||
                    (isFullDom ? dowMatch : isFullDow ? domMatch : domMatch || dowMatch));
            });
            if (dayFields.length === 0) {
                throw new Error(`Gap in schedule found ${month}/${day}`);
            }
            for (let hour = 0; hour <= 23; hour++) {
                const hourFields = dayFields.filter((f) => f.hour.has(hour));
                if (hourFields.length === 0) {
                    throw new Error(`Gap in schedule beginning at ${month}/${day} at hour ${hour}`);
                }
                for (let minute = 0; minute <= 59; minute++) {
                    const minuteFields = hourFields.filter((f) => f.minute.has(minute));
                    if (minuteFields.length === 1)
                        continue;
                    if (minuteFields.length > 1) {
                        throw new Error(`Overlap in cron expressions found at ${month}/${day} ` +
                            `${hour.toString().padStart(2, "0")}:${minute
                                .toString()
                                .padStart(2, "0")}`);
                    }
                    else {
                        throw new Error(`Gap in schedule beginning at ${month}/${day} ` +
                            `${hour.toString().padStart(2, "0")}:${minute
                                .toString()
                                .padStart(2, "0")}`);
                    }
                }
            }
        }
    }
}
exports.checkForCronGapsOrOverlaps = checkForCronGapsOrOverlaps;
